import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Calendar,
  User,
  Building,
  Briefcase,
  TrendingUp,
  DollarSign,
  ExternalLink,
  Target,
  CheckCircle,
  AlertCircle,
  XCircle,
  Phone,
  Copy,
  ChevronDown,
  ChevronUp,
  Activity,
  BarChart3,
  Award,
  Zap,
  Timer,
  UserCheck,
  FileText,
  Send,
  Mail,
  Trash2,
  RefreshCw,
  Eye,
  Edit3,
  Clock
} from 'lucide-react';
import { AdminSession } from '../types/admin.types';
import { isTasOnlyMode, isRoiAnalysisMode, getReportUrl, getOriginalReportUrl } from '../utils/calculatorPathUtils';
import { getReportById, StoredReportData } from '../../../services/reportStorage';
import { deleteSession, updateSessionLeadSource, fetchUniqueLeadSources, updateSessionConsultant, fetchUniqueConsultants } from '../services/adminService';
import { ConsultantInfo } from '../types/admin.types';
import { getReportVisitCounts } from '../../../services/reportAnalytics';

interface SessionDetailModalProps {
  session: AdminSession | null;
  onClose: () => void;
  onSelectSession?: (sessionId: string) => void;
  onDeleteSuccess?: () => void;
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({ session, onClose, onSelectSession, onDeleteSuccess }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'tas' | 'metrics' | 'roi' | 'actions'>('overview');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['scheduling', 'experience', 'business']));
  const [copied, setCopied] = useState(false);
  const [originalReport, setOriginalReport] = useState<StoredReportData | null>(null);
  const [loadingOriginalReport, setLoadingOriginalReport] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingLeadSource, setEditingLeadSource] = useState(false);
  const [leadSourceValue, setLeadSourceValue] = useState('');
  const [savingLeadSource, setSavingLeadSource] = useState(false);
  const [leadSourceSaved, setLeadSourceSaved] = useState(false);
  const [existingLeadSources, setExistingLeadSources] = useState<string[]>([]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(false);
  const [consultantValue, setConsultantValue] = useState('');
  const [savingConsultant, setSavingConsultant] = useState(false);
  const [consultantSaved, setConsultantSaved] = useState(false);
  const [consultantList, setConsultantList] = useState<ConsultantInfo[]>([]);
  const [filteredConsultants, setFilteredConsultants] = useState<ConsultantInfo[]>([]);
  const [showConsultantDropdown, setShowConsultantDropdown] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantInfo | null>(null);
  const [showAddConsultant, setShowAddConsultant] = useState(false);
  const [newConsultantName, setNewConsultantName] = useState('');
  const [newConsultantEmail, setNewConsultantEmail] = useState('');
  const consultantDropdownRef = useRef<HTMLDivElement>(null);
  const [reportVisitData, setReportVisitData] = useState<{
    totalViews: number;
    uniqueViewers: number;
    lastViewedAt?: Date;
  } | null>(null);

  // Fetch original report data for editing sessions
  useEffect(() => {
    const fetchOriginalReport = async () => {
      if (session?.isEditSession && session?.originalReportId) {
        setLoadingOriginalReport(true);
        try {
          const reportData = await getReportById(session.originalReportId);
          setOriginalReport(reportData);
        } catch (error) {
          console.error('Failed to fetch original report:', error);
          setOriginalReport(null);
        } finally {
          setLoadingOriginalReport(false);
        }
      } else {
        setOriginalReport(null);
        setLoadingOriginalReport(false);
      }
    };

    fetchOriginalReport();
  }, [session?.isEditSession, session?.originalReportId]);

  // Fetch report visit counts
  useEffect(() => {
    const fetchVisitCounts = async () => {
      if (session?.reportId) {
        try {
          const visitData = await getReportVisitCounts(session.reportId);
          setReportVisitData(visitData);
        } catch (error) {
          console.error('Failed to fetch report visit counts:', error);
        }
      }
    };

    fetchVisitCounts();
  }, [session?.reportId]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (consultantDropdownRef.current && !consultantDropdownRef.current.contains(event.target as Node)) {
        setShowConsultantDropdown(false);
      }
    };

    if (showConsultantDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showConsultantDropdown]);

  // Reset lead source editing when session changes and fetch existing lead sources
  useEffect(() => {
    setEditingLeadSource(false);
    setLeadSourceValue(session?.leadSource || '');
    setLeadSourceSaved(false);
    setShowCustomInput(false);
    setEditingConsultant(false);
    setConsultantSaved(false);
    setShowConsultantDropdown(false);
    setShowAddConsultant(false);

    // Set selected consultant based on session data
    if (session?.assignedConsultant) {
      setSelectedConsultant({
        email: session.assignedConsultant,
        name: session.assignedConsultantName || session.assignedConsultant
      });
      setConsultantValue(session.assignedConsultantName || session.assignedConsultant);
    } else {
      setSelectedConsultant(null);
      setConsultantValue('');
    }

    // Fetch existing lead sources and consultants
    const loadData = async () => {
      const sources = await fetchUniqueLeadSources();
      setExistingLeadSources(sources);
      const consultants = await fetchUniqueConsultants();
      setConsultantList(consultants);
      setFilteredConsultants(consultants);
    };
    loadData();
  }, [session]);

  const handleSaveLeadSource = async () => {
    if (!session) return;

    setSavingLeadSource(true);
    try {
      const result = await updateSessionLeadSource(session, leadSourceValue || null);
      if (result.success) {
        setLeadSourceSaved(true);
        setEditingLeadSource(false);
        setShowCustomInput(false);
        // Update the session object locally
        session.leadSource = leadSourceValue || undefined;
        setTimeout(() => setLeadSourceSaved(false), 3000);
      } else {
        alert('Failed to update lead source: ' + result.errors.join(', '));
      }
    } catch (error: any) {
      alert('Error updating lead source: ' + error.message);
    } finally {
      setSavingLeadSource(false);
    }
  };

  const handleSaveConsultant = async () => {
    if (!session) return;

    setSavingConsultant(true);
    try {
      const consultantEmail = selectedConsultant?.email || null;
      const consultantName = selectedConsultant?.name || null;
      const result = await updateSessionConsultant(session, consultantEmail, consultantName);
      if (result.success) {
        setConsultantSaved(true);
        setEditingConsultant(false);
        setShowConsultantDropdown(false);
        // Update the session object locally
        session.assignedConsultant = consultantEmail || undefined;
        session.assignedConsultantName = consultantName || undefined;
        setTimeout(() => setConsultantSaved(false), 3000);
      } else {
        alert('Failed to update consultant: ' + result.errors.join(', '));
      }
    } catch (error: any) {
      alert('Error updating consultant: ' + error.message);
    } finally {
      setSavingConsultant(false);
    }
  };

  const handleConsultantSearch = (searchTerm: string) => {
    setConsultantValue(searchTerm);
    setShowConsultantDropdown(true);

    if (!searchTerm.trim()) {
      setFilteredConsultants(consultantList);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = consultantList.filter(
      c => c.name.toLowerCase().includes(searchLower) ||
           c.email.toLowerCase().includes(searchLower)
    );
    setFilteredConsultants(filtered);
  };

  const selectConsultant = (consultant: ConsultantInfo | null) => {
    setSelectedConsultant(consultant);
    setConsultantValue(consultant ? consultant.name : '');
    setShowConsultantDropdown(false);
  };

  const handleAddNewConsultant = async () => {
    // Validate email
    if (!newConsultantEmail.endsWith('@roosterinc.com')) {
      alert('Email must be a @roosterinc.com address');
      return;
    }
    if (!newConsultantName.trim()) {
      alert('Name is required');
      return;
    }

    const newConsultant: ConsultantInfo = {
      name: newConsultantName.trim(),
      email: newConsultantEmail.trim().toLowerCase()
    };

    // Add to list and select
    setConsultantList([...consultantList, newConsultant]);
    selectConsultant(newConsultant);
    setShowAddConsultant(false);
    setNewConsultantName('');
    setNewConsultantEmail('');
  };


  if (!session) return null;

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Determine which TAS data to use - prefer current session, fall back to original report
  const effectiveResults = session.results?.tasComponents ? session.results : originalReport?.results;
  const effectiveTasComponents = effectiveResults?.tasComponents;
  const effectiveCurrentTAS = session.currentTAS || originalReport?.results?.currentTAS || 0;
  const effectiveProjectedTAS = session.projectedTAS || originalReport?.results?.projectedTAS;

  const getStatusIcon = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      case 'in_progress':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return 'Finalized';
      case 'complete':
        return 'Complete';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'complete':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const copySessionId = () => {
    navigator.clipboard.writeText(session.sessionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleDeleteSession = async () => {
    if (!session) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteSession(session);
      if (result.success) {
        onClose();
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      } else {
        alert('Failed to delete session: ' + result.errors.join(', '));
      }
    } catch (error: any) {
      alert('Error deleting session: ' + error.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getTASColor = (score: number) => {
    // Using consistent sky-blue color scheme from calculator
    return 'text-sky-600';
  };

  const getTASProgressColor = (score: number) => {
    // Use consistent sky-600 color for all components, matching calculator
    return '#0284c7'; // sky-600 - same as calculator component.color
  };

  const getComponentInfo = (key: string) => {
    const components: Record<string, { name: string; weight: string; description: string }> = {
      schedulingVelocity: {
        name: 'Scheduling Velocity (SV)',
        weight: '30%',
        description: 'Speed of coordinating and confirming interviews'
      },
      schedulingEfficiency: {
        name: 'Scheduling Efficiency (SE)',
        weight: '25%',
        description: 'Administrative efficiency in scheduling process'
      },
      candidateExperience: {
        name: 'Candidate Experience (CE)',
        weight: '20%',
        description: 'Quality of candidate scheduling experience'
      },
      schedulingIntegrity: {
        name: 'Scheduling Integrity (SI)',
        weight: '15%',
        description: 'Dependability of the interview scheduling process'
      },
      schedulingUtilization: {
        name: 'Scheduling Utilization (SU)',
        weight: '10%',
        description: 'Effective use of interviewer time and availability'
      }
    };
    return components[key] || { name: key, weight: '', description: '' };
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'metrics', label: 'Input Metrics', icon: Briefcase },
    { id: 'tas', label: 'TAS Analysis', icon: BarChart3 },
    { id: 'roi', label: 'ROI & Savings', icon: TrendingUp },
    { id: 'actions', label: 'Actions & Reports', icon: FileText }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-white md:rounded-xl shadow-2xl max-w-full md:max-w-6xl w-full h-full md:h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Session ID:</span>
                <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">
                  {session.sessionId.substring(0, 12)}...
                </code>
                <button
                  onClick={copySessionId}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Copy full ID"
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {isTasOnlyMode(session) && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  TAS
                </span>
              )}
              {isRoiAnalysisMode(session) && (
                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  ROI
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-sm font-medium border whitespace-nowrap flex items-center gap-1 ${getStatusColor(session.status)}`}>
                {getStatusIcon(session.status)}
                <span>{getStatusLabel(session.status)}</span>
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Primary Information Card */}
        <div className="px-4 md:px-6 py-4 bg-gradient-to-br from-sky-50 to-blue-50 border-b border-sky-100">
          {/* First Row: User/Org Info, Key Metrics, View Report Button */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 lg:gap-6">
            {/* User & Organization Info */}
            <div className="flex-shrink-0">
              <div className="flex items-start gap-3 mb-3">
                <User className="h-5 w-5 text-sky-600 mt-1 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 break-words">{session.userName}</h3>
                  <p className="text-sm text-gray-600 break-all">{session.userEmail}</p>
                  {session.formData?.contactTitle && (
                    <p className="text-sm text-gray-600">{session.formData.contactTitle}</p>
                  )}
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Phone className="h-3 w-3 flex-shrink-0" />
                    {session.formData?.contactPhone || 'Not provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building className="h-5 w-5 text-sky-600 mt-1 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 break-words">{session.organizationName}</p>
                  {session.formData?.industry && (
                    <p className="text-sm text-gray-600">{session.formData.industry}</p>
                  )}
                  {session.leadSource && (
                    <span className="inline-block mt-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full">
                      Source: {session.leadSource}
                    </span>
                  )}
                  {session.isEditSession && (
                    <div className="inline-flex items-center gap-2 mt-1">
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full">
                        Editing Session
                      </span>
                      {session.originalSessionId && onSelectSession && (
                        <button
                          onClick={() => onSelectSession(session.originalSessionId!)}
                          className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full hover:bg-indigo-200 transition-colors"
                        >
                          View Original Session
                          <Activity className="h-3 w-3" />
                        </button>
                      )}
                      {session.originalReportId && (
                        <a
                          href={getOriginalReportUrl(session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs px-2 py-1 rounded-full hover:bg-sky-200 transition-colors"
                        >
                          View Original Report
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="flex-1 grid grid-cols-3 gap-2 md:gap-4">
              {session.currentTAS && (
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Current TAS</p>
                  <p className={`text-3xl font-bold ${getTASColor(session.currentTAS)}`}>
                    {Math.round(session.currentTAS)}
                  </p>
                </div>
              )}
              {session.projectedTAS && (
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Projected TAS</p>
                  <p className="text-3xl font-bold text-green-600">
                    {Math.round(session.projectedTAS)}
                  </p>
                  {session.currentTAS && (
                    <p className="text-xs text-green-600">
                      +{Math.round(session.projectedTAS - session.currentTAS)}
                    </p>
                  )}
                </div>
              )}
              {(session.year1Savings || session.steadyStateSavings || session.totalAnnualSavings) && (
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Savings</p>
                  {session.year1Savings && session.steadyStateSavings ? (
                    <div>
                      <p className="text-lg font-bold text-green-600">
                        Y1: ${Math.round(session.year1Savings / 1000)}K
                      </p>
                      <p className="text-lg font-bold text-green-600">
                        SS: ${Math.round(session.steadyStateSavings / 1000)}K
                      </p>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-green-600">
                      ${Math.round((session.totalAnnualSavings || 0) / 1000)}K
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* View Report Button on far right */}
            {session.reportId && (
              <div className="flex-shrink-0">
                <a
                  href={getReportUrl(session)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Report
                </a>
              </div>
            )}
          </div>

          {/* Second Row: Engagement Metrics below the TAS scores */}
          <div className="flex justify-center mt-4">
            <div className="flex items-center gap-4 px-4 py-2 bg-white/70 rounded-lg">
              <div className="flex items-center gap-1.5 text-sm">
                <RefreshCw className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-gray-600">Returns:</span>
                <span className="font-medium text-gray-900">{session.returnCount || 0}</span>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex items-center gap-1.5 text-sm">
                <Eye className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-gray-600">Views:</span>
                <span className="font-medium text-gray-900">
                  {reportVisitData?.totalViews || 0}
                  {reportVisitData && reportVisitData.uniqueViewers > 0 && (
                    <span className="text-gray-500 ml-1">
                      ({reportVisitData.uniqueViewers})
                    </span>
                  )}
                </span>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex items-center gap-1.5 text-sm">
                <Edit3 className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-gray-600">Edits:</span>
                <span className="font-medium text-gray-900">{session.editCount || 0}</span>
              </div>
              {session.totalTimeSpent && session.totalTimeSpent > 0 && (
                <>
                  <div className="h-4 w-px bg-gray-300" />
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-gray-600">Time:</span>
                    <span className="font-medium text-gray-900">
                      {session.totalTimeSpent < 60
                        ? `${session.totalTimeSpent}s`
                        : `${Math.round(session.totalTimeSpent / 60)}m`}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-200 px-4 md:px-6 bg-white scrollbar-hide">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-sky-500 text-sky-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium text-sm md:text-base">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Timeline */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-gray-500" />
                  Assessment Timeline
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Started</p>
                    <p className="font-medium text-gray-900">{formatDate(session.startedAt)}</p>
                  </div>
                  {session.completedAt && (
                    <div>
                      <p className="text-sm text-gray-600">Completed</p>
                      <p className="font-medium text-gray-900">{formatDate(session.completedAt)}</p>
                    </div>
                  )}
                  {session.finalizedAt && (
                    <div>
                      <p className="text-sm text-gray-600">Finalized</p>
                      <p className="font-medium text-gray-900">{formatDate(session.finalizedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assessment Details */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="h-5 w-5 text-gray-500" />
                  Assessment Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Assessment Type</p>
                    <p className="font-medium text-gray-900">
                      {session.assessmentType === 'self' ? 'Self Assessment' : 'Consultant Led'}
                    </p>
                  </div>
                  {session.onBehalfOf && (
                    <div>
                      <p className="text-sm text-gray-600">On Behalf Of</p>
                      <p className="font-medium text-gray-900">{session.organizationName}</p>
                    </div>
                  )}
                  {session.isRoosterAssessment && (
                    <div>
                      <p className="text-sm text-gray-600">Rooster Assessment</p>
                      <p className="font-medium text-purple-600">Yes</p>
                    </div>
                  )}
                  {(session.highestStepReached || session.currentStep) && (
                    <div>
                      <p className="text-sm text-gray-600">Last Step Reached</p>
                      <p className="font-medium text-gray-900">Step {session.highestStepReached || session.currentStep}</p>
                    </div>
                  )}
                  
                  {/* Lead Source Field with Inline Editing */}
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-600 mb-1">Lead Source</p>
                    {!editingLeadSource ? (
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {session.leadSource || <span className="text-gray-400 italic">Not set</span>}
                        </p>
                        <button
                          onClick={() => {
                            setEditingLeadSource(true);
                            setLeadSourceValue(session.leadSource || '');
                            setShowCustomInput(false);
                          }}
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        {leadSourceSaved && (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="h-3 w-3" />
                            Saved
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          {!showCustomInput ? (
                            <select
                              value={leadSourceValue}
                              onChange={(e) => {
                                if (e.target.value === '__custom__') {
                                  setShowCustomInput(true);
                                  setLeadSourceValue('');
                                } else {
                                  setLeadSourceValue(e.target.value);
                                }
                              }}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              autoFocus
                            >
                              <option value="">-- Clear --</option>
                              {existingLeadSources.map(source => (
                                <option key={source} value={source}>{source}</option>
                              ))}
                              <option value="__custom__">+ Add new...</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={leadSourceValue}
                              onChange={(e) => setLeadSourceValue(e.target.value)}
                              placeholder="Enter new lead source"
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              autoFocus
                            />
                          )}
                        </div>
                        <button
                          onClick={handleSaveLeadSource}
                          disabled={savingLeadSource}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md transition-colors disabled:opacity-50"
                        >
                          {savingLeadSource ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingLeadSource(false);
                            setLeadSourceValue(session.leadSource || '');
                            setShowCustomInput(false);
                          }}
                          disabled={savingLeadSource}
                          className="px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm rounded-md transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Assigned Consultant Field */}
                  <div className="sm:col-span-2 relative">
                    <p className="text-sm text-gray-600 mb-1">Assigned Consultant</p>
                    {!editingConsultant ? (
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {session.assignedConsultantName || session.assignedConsultant || <span className="text-gray-400 italic">Not assigned</span>}
                        </p>
                        <button
                          onClick={() => {
                            setEditingConsultant(true);
                            setConsultantValue(session.assignedConsultantName || session.assignedConsultant || '');
                            if (session.assignedConsultant) {
                              setSelectedConsultant({
                                email: session.assignedConsultant,
                                name: session.assignedConsultantName || session.assignedConsultant
                              });
                            }
                          }}
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        {consultantSaved && (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="h-3 w-3" />
                            Saved
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 relative">
                        <div className="flex items-start gap-2">
                          <div className="flex-1" ref={consultantDropdownRef} style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={consultantValue}
                              onChange={(e) => handleConsultantSearch(e.target.value)}
                              onFocus={() => setShowConsultantDropdown(true)}
                              placeholder="Search by name or email..."
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              autoFocus
                            />
                            {selectedConsultant && (
                              <div className="text-xs text-gray-500 mt-1">
                                Selected: {selectedConsultant.email}
                              </div>
                            )}

                            {/* Dropdown */}
                            {showConsultantDropdown && (
                              <div
                                className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200"
                                style={{
                                  maxHeight: '180px',
                                  overflowY: 'auto',
                                  overflowX: 'hidden'
                                }}
                              >
                                {/* Clear selection option */}
                                <button
                                  type="button"
                                  onClick={() => selectConsultant(null)}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b sticky top-0 bg-white"
                                >
                                  <span className="text-gray-500 italic">Clear selection</span>
                                </button>

                                {/* Filtered consultants */}
                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                  {filteredConsultants.length > 0 ? (
                                    filteredConsultants.map(consultant => (
                                      <button
                                        key={consultant.email}
                                        type="button"
                                        onClick={() => selectConsultant(consultant)}
                                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
                                      >
                                        <div className="font-medium">{consultant.name}</div>
                                        <div className="text-xs text-gray-500">{consultant.email}</div>
                                      </button>
                                    ))
                                  ) : consultantValue ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                      No matching consultants found
                                    </div>
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                      Start typing to search or add a new consultant
                                    </div>
                                  )}
                                </div>

                                {/* Add new consultant option */}
                                <div className="sticky bottom-0 bg-white border-t">
                                  {consultantValue && !consultantValue.endsWith('@roosterinc.com') && consultantValue.includes('@') && (
                                    <div className="px-3 py-2 text-sm text-red-500">
                                      Must be a @roosterinc.com email address
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowAddConsultant(true);
                                      setShowConsultantDropdown(false);

                                      // Pre-fill fields based on what user typed
                                      if (consultantValue) {
                                        // Check if it looks like an email
                                        if (consultantValue.includes('@')) {
                                          setNewConsultantEmail(consultantValue.trim());
                                          // Try to extract name from email if possible
                                          const emailPart = consultantValue.split('@')[0];
                                          const name = emailPart
                                            .replace(/[._-]/g, ' ')
                                            .split(' ')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                            .join(' ');
                                          setNewConsultantName(name);
                                        } else {
                                          // Assume it's a name
                                          setNewConsultantName(consultantValue.trim());
                                          // Generate email from name
                                          const emailName = consultantValue.trim()
                                            .toLowerCase()
                                            .replace(/\s+/g, '.');
                                          setNewConsultantEmail(emailName + '@roosterinc.com');
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-green-50 text-sm text-green-600 font-medium"
                                  >
                                    + Add new consultant...
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={handleSaveConsultant}
                            disabled={savingConsultant}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md transition-colors disabled:opacity-50"
                          >
                            {savingConsultant ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingConsultant(false);
                              setConsultantValue(session.assignedConsultantName || session.assignedConsultant || '');
                              setShowConsultantDropdown(false);
                              setShowAddConsultant(false);
                              if (session.assignedConsultant) {
                                setSelectedConsultant({
                                  email: session.assignedConsultant,
                                  name: session.assignedConsultantName || session.assignedConsultant
                                });
                              } else {
                                setSelectedConsultant(null);
                              }
                            }}
                            disabled={savingConsultant}
                            className="px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm rounded-md transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>

                        {/* Add new consultant form */}
                        {showAddConsultant && (
                          <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-2">Add New Consultant</p>
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={newConsultantName}
                                onChange={(e) => setNewConsultantName(e.target.value)}
                                placeholder="Full Name"
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <input
                                type="email"
                                value={newConsultantEmail}
                                onChange={(e) => setNewConsultantEmail(e.target.value)}
                                placeholder="email@roosterinc.com"
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={handleAddNewConsultant}
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md"
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddConsultant(false);
                                    setNewConsultantName('');
                                    setNewConsultantEmail('');
                                    setShowConsultantDropdown(true); // Show dropdown again
                                  }}
                                  className="px-3 py-1 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm rounded-md"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* TAS Analysis Tab */}
          {activeTab === 'tas' && (
            <div className="space-y-6">
              {/* Show loading state while fetching original report */}
              {loadingOriginalReport ? (
                <div className="bg-white p-12 rounded-lg shadow-sm">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading TAS analysis...</p>
                  </div>
                </div>
              ) : !session.results?.tasComponents && !originalReport?.results?.tasComponents ? (
                <div className="bg-white p-12 rounded-lg shadow-sm">
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">TAS analysis was not completed for this session</p>
                  </div>
                </div>
              ) : (
                <>
                {/* Show info banner if using original report data */}
                {!session.results?.tasComponents && originalReport?.results?.tasComponents && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                    <div className="flex items-center gap-2 text-blue-800">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Showing TAS analysis from original session (editing session did not modify TAS components)
                      </span>
                    </div>
                  </div>
                )}

              {/* TAS Score Overview */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-sky-500" />
                  TAS Score Overview
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-lg border border-sky-100">
                    <p className="text-3xl font-bold bg-gradient-to-r from-sky-600 to-cyan-500 bg-clip-text text-transparent">{Math.round(effectiveCurrentTAS)}</p>
                    <p className="text-sm text-gray-600 mt-1">Current Score</p>
                  </div>
                  {effectiveProjectedTAS && (
                    <>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-600">{Math.round(effectiveProjectedTAS)}</p>
                        <p className="text-sm text-gray-600 mt-1">Projected Score</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-600">+{Math.round(effectiveProjectedTAS - effectiveCurrentTAS)}</p>
                        <p className="text-sm text-gray-600 mt-1">Improvement</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* TAS Components */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-sky-500" />
                  TAS Component Breakdown
                </h3>
                <div className="space-y-5">
                  {effectiveTasComponents && (() => {
                    // Define the weight order for each component
                    const componentWeights: Record<string, number> = {
                      schedulingVelocity: 30,
                      schedulingEfficiency: 25,
                      candidateExperience: 20,
                      schedulingIntegrity: 15,
                      schedulingUtilization: 10
                    };

                    // Sort components by weight (highest to lowest)
                    const sortedComponents = Object.entries(effectiveTasComponents).sort(([keyA], [keyB]) => {
                      const weightA = componentWeights[keyA] || 0;
                      const weightB = componentWeights[keyB] || 0;
                      return weightB - weightA; // Descending order
                    });

                    return sortedComponents.map(([key, value]) => {
                      const componentInfo = getComponentInfo(key);
                      return (
                        <div key={key} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-800">{componentInfo.name}</span>
                                <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full font-medium">
                                  Weight: {componentInfo.weight}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{componentInfo.description}</p>
                            </div>
                            <span className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-cyan-500 bg-clip-text text-transparent ml-4">
                              {Math.round(value)}
                            </span>
                          </div>
                          <div className="relative w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                              style={{
                                width: `${value}%`,
                                backgroundColor: getTASProgressColor(value)
                              }}
                            />
                            {/* Tick marks for reference */}
                            <div className="absolute inset-0 flex justify-between px-0.5">
                              <div className="w-px h-full bg-white opacity-50" style={{ marginLeft: '20%' }} />
                              <div className="w-px h-full bg-white opacity-50" style={{ marginLeft: '20%' }} />
                              <div className="w-px h-full bg-white opacity-50" style={{ marginLeft: '20%' }} />
                              <div className="w-px h-full bg-white opacity-50" style={{ marginLeft: '20%' }} />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {/* Legend */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-2">Score Interpretation:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                    <div>0-40: Needs Improvement</div>
                    <div>61-80: Good</div>
                    <div>41-60: Average</div>
                    <div>81-100: Excellent</div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic">All components use consistent weighting to calculate your overall TAS score</p>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {/* Input Metrics Tab */}
          {activeTab === 'metrics' && (
            <div className="space-y-4">
              {!session.formData ? (
                <div className="bg-white p-12 rounded-lg shadow-sm">
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Input metrics were not collected for this session</p>
                  </div>
                </div>
              ) : (
                <>
              {/* Business Metrics */}
              <div className="bg-white rounded-lg shadow-sm">
                <button
                  onClick={() => toggleSection('business')}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-gray-500" />
                    Business Metrics
                  </h3>
                  {expandedSections.has('business') ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedSections.has('business') && (
                  <div className="px-6 pb-4">
                    {!session.formData.annualHires && !session.formData.timeToFill && 
                     !session.formData.costPerHire && !session.formData.dailyVacancyCost && 
                     !session.formData.avgSalary && !session.formData.tasImprovement ? (
                      <div className="py-8 text-center">
                        <p className="text-gray-400 text-sm">No business metrics data available</p>
                      </div>
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-8 gap-y-3">
                      {session.formData.annualHires !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Annual Hires</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.annualHires}</span>
                        </div>
                      )}
                      {session.formData.timeToFill !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Time to Fill</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.timeToFill} days</span>
                        </div>
                      )}
                      {session.formData.costPerHire !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Cost per Hire</span>
                          <span className="font-medium text-gray-900 text-right">${Number(session.formData.costPerHire).toLocaleString()}</span>
                        </div>
                      )}
                      {session.formData.dailyVacancyCost !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Daily Vacancy Cost</span>
                          <span className="font-medium text-gray-900 text-right">${Number(session.formData.dailyVacancyCost).toLocaleString()}</span>
                        </div>
                      )}
                      {session.formData.avgSalary !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Average Salary</span>
                          <span className="font-medium text-gray-900 text-right">${Number(session.formData.avgSalary).toLocaleString()}</span>
                        </div>
                      )}
                      {session.formData.tasImprovement !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">TAS Improvement Goal</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.tasImprovement}%</span>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}
              </div>

              {/* Scheduling Performance */}
              <div className="bg-white rounded-lg shadow-sm">
                <button
                  onClick={() => toggleSection('scheduling')}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Timer className="h-5 w-5 text-gray-500" />
                    Scheduling Performance
                  </h3>
                  {expandedSections.has('scheduling') ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedSections.has('scheduling') && (
                  <div className="px-6 pb-4">
                    {!session.formData.avgConfirmationTime && !session.formData.adminTimePerHire && 
                     !session.formData.reschedulingRate && !session.formData.interviewNoShowRate && 
                     (!session.formData.schedulingMethods || session.formData.schedulingMethods.length === 0) ? (
                      <div className="py-8 text-center">
                        <p className="text-gray-400 text-sm">No scheduling performance data available</p>
                      </div>
                    ) : (
                    <>
                    {session.formData.schedulingMethods && session.formData.schedulingMethods.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Scheduling Methods</p>
                        <div className="flex flex-wrap gap-2">
                          {session.formData.schedulingMethods.map((method, idx) => (
                            <span key={idx} className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm">
                              {method}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-8 gap-y-3">
                      {session.formData.avgConfirmationTime !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Avg Confirmation Time</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.avgConfirmationTime} hours</span>
                        </div>
                      )}
                      {session.formData.adminTimePerHire !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Admin Time per Hire</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.adminTimePerHire} hours</span>
                        </div>
                      )}
                      {session.formData.reschedulingRate !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Rescheduling Rate</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.reschedulingRate}%</span>
                        </div>
                      )}
                      {session.formData.interviewNoShowRate !== undefined && (
                        <div className="flex justify-between items-center gap-4 py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-600">No-Show Rate</span>
                          <span className="font-medium text-gray-900 text-right">{session.formData.interviewNoShowRate}%</span>
                        </div>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                )}
              </div>

              {/* Experience Metrics */}
              <div className="bg-white rounded-lg shadow-sm">
                <button
                  onClick={() => toggleSection('experience')}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-gray-500" />
                    Experience Metrics
                  </h3>
                  {expandedSections.has('experience') ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedSections.has('experience') && (
                  <div className="px-6 pb-4">
                    {!session.formData.candidateSatisfaction && !session.formData.interviewerSatisfaction && 
                     !session.formData.interviewerAvailability ? (
                      <div className="py-8 text-center">
                        <p className="text-gray-400 text-sm">No experience metrics data available</p>
                      </div>
                    ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {session.formData.candidateSatisfaction !== undefined && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">Candidate Satisfaction</p>
                          <p className="text-2xl font-bold text-gray-900">{session.formData.candidateSatisfaction}/10</p>
                        </div>
                      )}
                      {session.formData.interviewerSatisfaction !== undefined && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">Interviewer Satisfaction</p>
                          <p className="text-2xl font-bold text-gray-900">{session.formData.interviewerSatisfaction}/10</p>
                        </div>
                      )}
                      {session.formData.interviewerAvailability !== undefined && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">Interviewer Availability</p>
                          <p className="text-2xl font-bold text-gray-900">{session.formData.interviewerAvailability}/10</p>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          )}

          {/* ROI & Savings Tab */}
          {activeTab === 'roi' && (
            <div className="space-y-6">
              {isTasOnlyMode(session) ? (
                <div className="bg-white p-12 rounded-lg shadow-sm">
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                    <p className="text-gray-600">Not applicable for TAS only calculation sessions</p>
                    <p className="text-sm text-gray-500 mt-2">This session used TAS-only mode without ROI analysis</p>
                  </div>
                </div>
              ) : !session.year1Savings && !session.steadyStateSavings && !session.totalAnnualSavings && (!session.results?.savings || Object.values(session.results.savings).every(value => value === 0)) ? (
                <div className="bg-white p-12 rounded-lg shadow-sm">
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">ROI calculations were not completed for this session</p>
                  </div>
                </div>
              ) : (
                <>
              {/* ROI Analysis Summary */}
              {(session.year1ROI || session.steadyStateROI || session.paybackMonth) && (
                <div className="bg-gradient-to-br from-gray-50 to-sky-50 p-6 rounded-lg shadow-sm border border-gray-100 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">ROI Analysis Summary</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Expected ROI based on balanced assumptions.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {session.year1ROI && (
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600 mb-1">Year 1 ROI</p>
                        <p className="text-3xl font-bold text-orange-600">{session.year1ROI.toFixed(1)}x</p>
                        <p className="text-xs text-gray-500 mt-1">With ramp-up</p>
                      </div>
                    )}
                    {session.steadyStateROI && (
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600 mb-1">Steady-State ROI</p>
                        <p className="text-3xl font-bold text-green-600">{session.steadyStateROI.toFixed(1)}x</p>
                        <p className="text-xs text-gray-500 mt-1">Years 2+</p>
                      </div>
                    )}
                    {session.paybackMonth && (
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600 mb-1">Payback Period</p>
                        <p className="text-3xl font-bold text-sky-600">{session.paybackMonth}</p>
                        <p className="text-xs text-gray-500 mt-1">Months</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Key Metrics Comparison */}
              {session.results?.metrics && (
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-gray-500" />
                    Key Metrics Comparison
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-3 text-left font-medium text-gray-700">Key Metrics</th>
                          <th className="p-3 text-center font-medium text-gray-700">
                            <div>Current</div>
                            <div className="text-xs font-normal text-gray-500">(per hire)</div>
                          </th>
                          <th className="p-3 text-center font-medium text-sky-700">
                            <div>Improved</div>
                            <div className="text-xs font-normal text-sky-500">(per hire)</div>
                          </th>
                          <th className="p-3 text-right font-medium text-gray-700">Change</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {session.results.metrics.current.timeToFill && (
                          <tr className="border-t">
                            <td className="p-3">Time to Fill</td>
                            <td className="p-3 text-center">{Math.round(session.results.metrics.current.timeToFill)} days</td>
                            <td className="p-3 text-center">{Math.round(session.results.metrics.improved.timeToFill)} days</td>
                            <td className="p-3 text-right text-green-600 font-bold">
                              -{Math.round((session.results.metrics.current.timeToFill - session.results.metrics.improved.timeToFill) / session.results.metrics.current.timeToFill * 100)}%
                            </td>
                          </tr>
                        )}
                        {session.results.metrics.current.costPerHire && (
                          <tr className="border-t">
                            <td className="p-3">Cost per Hire</td>
                            <td className="p-3 text-center">${Math.round(session.results.metrics.current.costPerHire).toLocaleString()}</td>
                            <td className="p-3 text-center">${Math.round(session.results.metrics.improved.costPerHire).toLocaleString()}</td>
                            <td className="p-3 text-right text-green-600 font-bold">
                              -{Math.round((session.results.metrics.current.costPerHire - session.results.metrics.improved.costPerHire) / session.results.metrics.current.costPerHire * 100)}%
                            </td>
                          </tr>
                        )}
                        {session.results.metrics.current.vacancyCost && (
                          <tr className="border-t">
                            <td className="p-3">Vacancy Cost</td>
                            <td className="p-3 text-center">${Math.round(session.results.metrics.current.vacancyCost).toLocaleString()}</td>
                            <td className="p-3 text-center">${Math.round(session.results.metrics.improved.vacancyCost).toLocaleString()}</td>
                            <td className="p-3 text-right text-green-600 font-bold">
                              -{Math.round((session.results.metrics.current.vacancyCost - session.results.metrics.improved.vacancyCost) / session.results.metrics.current.vacancyCost * 100)}%
                            </td>
                          </tr>
                        )}
                        {session.formData?.interviewNoShowRate && (
                          <tr className="border-t">
                            <td className="p-3">Interview No-Show Rate</td>
                            <td className="p-3 text-center">{Math.round(Number(session.formData.interviewNoShowRate))}%</td>
                            <td className="p-3 text-center">
                              {Math.round(Number(session.formData.interviewNoShowRate) * (1 - Math.min(0.60 * (Number(session.formData.tasImprovement || 20) / 30), 0.75)))}%
                            </td>
                            <td className="p-3 text-right text-green-600 font-bold">
                              -{Math.round(Math.min(0.60 * (Number(session.formData.tasImprovement || 20) / 30), 0.75) * 100)}%
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 italic">
                    Per-hire improvements × {session.formData?.annualHires?.toLocaleString() || '0'} annual hires = total annual impact
                  </p>
                </div>
              )}

              {/* Savings Overview */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-gray-500" />
                  Savings Overview
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  {session.year1Savings && (
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600 uppercase tracking-wide">First Year Savings</p>
                      <p className="text-3xl font-bold text-green-700 mt-2">
                        ${Math.round(session.year1Savings).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {session.steadyStateSavings && (
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600 uppercase tracking-wide">Steady State Savings</p>
                      <p className="text-3xl font-bold text-green-700 mt-2">
                        ${Math.round(session.steadyStateSavings).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {!session.year1Savings && !session.steadyStateSavings && session.totalAnnualSavings && (
                    <div className="text-center p-6 bg-green-50 rounded-lg col-span-2">
                      <p className="text-sm text-green-600 uppercase tracking-wide">Total Annual Savings</p>
                      <p className="text-3xl font-bold text-green-700 mt-2">
                        ${Math.round(session.totalAnnualSavings).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Savings Breakdown */}
              {session.results?.savings && (
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Award className="h-5 w-5 text-gray-500" />
                    Savings Breakdown
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {session.results?.enhancedSavings ? 'Steady State Values (with adoption rates applied)' : 'Base Calculation Values'}
                  </p>
                  <div className="space-y-3">
                    {(() => {
                      // Use enhanced savings if available (with adoption rates applied)
                      // Otherwise fall back to base savings
                      const enhancedSavings = session.results?.enhancedSavings;
                      const steadyStateSavings = enhancedSavings?.steadyStateSavings || session.results.savings;
                      
                      return (
                        <>
                          {steadyStateSavings.timeToFillSavings > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Vacancy Cost Reduction</span>
                              <span className="font-medium text-green-600">
                                ${Math.round(steadyStateSavings.timeToFillSavings).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {steadyStateSavings.costPerHireSavings > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Hiring Cost Reduction</span>
                              <span className="font-medium text-green-600">
                                ${Math.round(steadyStateSavings.costPerHireSavings).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {steadyStateSavings.productivityGain > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Productivity Gains</span>
                              <span className="font-medium text-green-600">
                                ${Math.round(steadyStateSavings.productivityGain).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {steadyStateSavings.schedulingTimeSavings > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Admin Time Savings</span>
                              <span className="font-medium text-green-600">
                                ${Math.round(steadyStateSavings.schedulingTimeSavings).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {steadyStateSavings.noShowReductionSavings > 0 && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">No-Show Reduction Savings</span>
                              <span className="font-medium text-green-600">
                                ${Math.round(steadyStateSavings.noShowReductionSavings).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          )}

          {/* Actions & Reports Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-6">
              {/* Report Status */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-gray-500" />
                  Report Status
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Report Generated</p>
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {session.reportGenerated ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Yes
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-gray-400" />
                          No
                        </>
                      )}
                    </p>
                  </div>
                  {session.reportId && (
                    <div>
                      <p className="text-sm text-gray-600">Report ID</p>
                      <p className="font-medium text-gray-900">{session.reportId}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Communications */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Send className="h-5 w-5 text-gray-500" />
                  Communications
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Emails Sent</p>
                    <p className="font-medium text-gray-900">{session.emailsSent || 0}</p>
                  </div>
                  {session.emailRecipients && session.emailRecipients.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Email Recipients (including CC)</p>
                      <div className="flex flex-wrap gap-2">
                        {session.emailRecipients.map((email, index) => (
                          <span 
                            key={index}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                          >
                            <Mail className="h-3 w-3" />
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Session */}
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border-2 border-red-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-500" />
                  Delete Session
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Permanently delete this session and all associated data including reports, analytics, and visit tracking.
                </p>
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Session
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-2">
                        ⚠️ Are you sure you want to delete this session?
                      </p>
                      <p className="text-xs text-red-700 mb-2">
                        This will permanently remove:
                      </p>
                      <ul className="text-xs text-red-700 ml-4 list-disc space-y-1">
                        <li>Session ID: {session.sessionId.substring(0, 12)}...</li>
                        <li>Organization: {session.organizationName}</li>
                        <li>User: {session.userName} ({session.userEmail})</li>
                        <li>All associated reports and analytics data</li>
                      </ul>
                      <p className="text-xs font-medium text-red-800 mt-3">
                        This action cannot be undone.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteSession}
                        disabled={isDeleting}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                      >
                        {isDeleting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4" />
                            Confirm Delete
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;